import React, { useReducer, useEffect, useState, useLayoutEffect } from "react";
import StackGrid from "react-stack-grid";
import client, { makeGet } from "@culturehq/client";
import { ActionButton, Button, Icon, Loader, Thumbnail } from "@culturehq/components";

import { useProfile } from "app/context";
import { Trans, getImageFromStory, getDuration } from "app/utils";
import imagesSizes from "constants/imagesSizes";

import BodyTags from "components/common/BodyTags";
import ErrorBoundary from "components/common/ErrorBoundary";
import ErrorMessage from "components/common/ErrorMessage";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import LightboxStories from "components/common/gallery/LightboxStories";
import PostStory from "components/stories/post-stories-v2/PostStory";
import StoryItemPlaceholder from "./StoryItemPlaceholder";
import LastIntersectedElement from "./LastIntersectedElement";

const initialState = {
  appending: null,
  appendingImages: false,
  error: null,
  fetching: false,
  pagination: {},
  stories: []
};

const sortBySelected = stories => (
  [...stories.sort((a, b) => {
    if (a.selected === b.selected) {
      return 0;
    }

    return (a.selected ? -1 : 1);
  })]
);

const sortByUnselected = stories => (
  [...stories.sort((a, b) => {
    if (a.selected === b.selected) {
      return 0;
    }

    return (a.selected ? 1 : -1);
  })]
);

const sortByDate = stories => (
  [...stories.sort((a, b) => {
    if (a.createdAt === b.createdAt) {
      return 0;
    }

    return (a.createdAt < b.createdAt ? 1 : -1);
  })]
);

const sort = (sortBy, stories) => {
  if (sortBy === "selected") {
    return sortBySelected(stories);
  }

  if (sortBy === "unselected") {
    return sortByUnselected(stories);
  }

  return stories;
};

const reducer = (state, action) => {
  switch (action.type) {
    case "appendStart":
      return { ...state, appending: state.pagination.currentPage + 1, appendingImages: true };
    case "appendSuccess":
      return {
        ...state,
        appending: null,
        pagination: action.response.pagination,
        stories: sort(action.sortBy, action.selectedStories.length > 0
          ? [...state.stories, ...action.response.stories].map(story => (
            {
              ...story,
              selected: !!action.selectedStories.find(
                selectedStory => selectedStory.id === story.id
              )
            }
          )) : action.response.stories)
      };
    case "appendError":
      return { ...state, appending: null, error: action.error };
    case "fetchStart":
      return { ...state, error: null, fetching: true };
    case "fetchSuccess":
      return {
        ...state,
        error: null,
        fetching: false,
        pagination: action.response.pagination,
        stories: sort(action.sortBy, action.selectedStories.length > 0
          ? action.response.stories.map(story => (
            {
              ...story,
              selected: !!action.selectedStories.find(
                selectedStory => selectedStory.id === story.id
              )
            }
          )) : action.response.stories)
      };
    case "fetchError":
      return { ...state, error: action.error, fetching: false };
    case "storyUpdate":
      return {
        ...state,
        stories: state.stories.map(story => {
          if (story.id === action.storyId) {
            return { ...story, ...action.updates };
          }
          return story;
        })
      };
    case "storyRemove":
      return {
        ...state,
        stories: state.stories.filter(story => (
          story.id !== action.storyId
        ))
      };
    case "storyCommentsUpdated":
      return {
        ...state,
        stories: state.stories.map(story => {
          if (story.id === action.storyId) {
            return { ...story, commentCount: action.numberOfComments };
          }
          return story;
        })
      };
    case "setStories":
      return {
        ...state,
        stories: action.stories
      };
    case "sortBySelected":
      return {
        ...state,
        stories: sortBySelected(action.stories || state.stories)
      };
    case "sortByUnselected":
      return {
        ...state,
        stories: sortByUnselected(action.stories || state.stories)
      };
    case "sortByDate":
      return {
        ...state,
        stories: sortByDate(action.stories || state.stories)
      };
    case "storySelected":
      return {
        ...state,
        stories: state.stories.map(
          story => (story.id === action.story.id ? { ...story, selected: true } : story)
        )
      };
    case "storyUnselected":
      return {
        ...state,
        stories: state.stories.map(
          story => (story.id === action.story.id ? { ...story, selected: false } : story)
        )
      };
    default:
      return state;
  }
};

const getSortDirection = sortBy => (sortBy === "selected" ? "DESC" : "ASC");

const useFetchStories = (dispatch, selectedStories, landingPageId, sortBy) => useEffect(
  () => {
    let cancelled = false;

    dispatch({ type: "fetchStart" });
    client.listStories({ landingPageId, sortDirection: getSortDirection(sortBy) })
      .then(response => {
        if (!cancelled) {
          dispatch({ type: "fetchSuccess", response, selectedStories, sortBy });
        }
      })
      .catch(error => {
        if (!cancelled) {
          dispatch({ type: "fetchError", error });
        }
      });

    return () => {
      cancelled = true;
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [dispatch, landingPageId, sortBy]
);

const useAppendStories = (
  dispatch, selectedStories, landingPageId, sortBy, appending, query) => useEffect(
  () => {
    if (appending) {
      let cancelled = false;
      if (query) {
        makeGet("/autocomplete/stories", { page: appending, query })
          .then(response => {
            if (!cancelled) {
              dispatch({ type: "appendSuccess", response, selectedStories, sortBy });
            }
          })
          .catch(error => {
            if (!cancelled) {
              dispatch({ type: "appendError", error });
            }
          });
      } else {
        client.listStories({ page: appending,
          landingPageId,
          sortDirection: getSortDirection(sortBy) })
          .then(response => {
            if (!cancelled) {
              dispatch({ type: "appendSuccess", response, selectedStories, sortBy });
            }
          })
          .catch(error => {
            if (!cancelled) {
              dispatch({ type: "appendError", error });
            }
          });

        return () => {
          cancelled = true;
        };
      }
    }

    return undefined;
  },
  [dispatch, appending, selectedStories, landingPageId, sortBy, query]
);

const getColumnWidth = storiesNumber => {
  if (window.innerWidth >= 768 && storiesNumber !== 1) {
    return "50%";
  }

  return "100%";
};

const StoryThumbnail = ({ story, index, onClick, selectStory, unselectStory }) => (
  <div className={`PhotoGroup__thumbnail Story ${story.selected ? "Story__Selected" : ""}`} onClick={() => onClick(index)} role="button" tabIndex={0}>
    <img
      className="story--image"
      src={story.media.mediaType === "video" ? story.media?.thumbnail : getImageFromStory(imagesSizes.NORMAL, story)}
      alt={story.imageAlt || story.question.question}
    />
    <div className="Story__information--profile">
      <div className="background-container">
        <div className="Story__header">
          <div className="Story__creator">
            <Thumbnail image={story.creator.avatar.thumbUrl} />
            <div>
              <p>{story.creator.name}</p>
              <p>{story.creator.title}</p>
            </div>
          </div>
          {story.media.mediaType === "video" && (
            <span className="Story__badge">
              <Icon icon="play-no-border" />
              {getDuration(story.media?.duration)}
            </span>
          )}
        </div>
        <span className="Story__question">{story.question.question}</span>
        <LazyEditorOutput output={story.body} showTags={false} />
        <BodyTags hideLinks showDefaultTag tags={story.bodyTags} />
        <div className="footer">
          {story.selected && (
            <ActionButton
              onClick={event => {
                event.stopPropagation();
                unselectStory(story);
              }}
            >
              <Icon icon="checkmark" />
              <Trans>Selected</Trans>
            </ActionButton>
          )}
        </div>
      </div>
    </div>
    <div className="Story__information--profile Story__answer">
      <div className="background-container">
        <LazyEditorOutput output={story.body} showTags={false} />
        <div className="footer">
          {story.selected
            ? (
              <ActionButton
                onClick={event => {
                  event.stopPropagation();
                  unselectStory(story);
                }}
              >
                <Icon icon="checkmark" />
                <Trans>Selected</Trans>
              </ActionButton>
            ) : (
              <Button
                onClick={event => {
                  event.stopPropagation();
                  selectStory(story);
                }}
              >
                <Icon icon="ios-plus" />
                <Trans>Select Story</Trans>
              </Button>
            )}
        </div>
      </div>
    </div>
  </div>
);

const Stories = ({
  landingPageId,
  feedRef,
  onSelected,
  onUnselected,
  sortBy,
  selectedStories,
  stories,
  query
}) => {
  const { profile } = useProfile();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [activeStory, setActiveStory] = useState(undefined);
  const [activeStoryQuestion] = useState(null);
  const [openShareStoryModal] = useState(null);
  const [columnWidth, setColumnWidth] = useState(getColumnWidth());
  const { createYourOwnStory } = profile.organization;

  useFetchStories(dispatch, selectedStories, landingPageId, sortBy);
  useAppendStories(dispatch, selectedStories, landingPageId, sortBy, state.appending, query);

  useLayoutEffect(() => {
    const updateSize = () => {
      setColumnWidth(getColumnWidth());
    };
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(
    () => {
      if (sortBy === "selected") {
        dispatch({ type: "sortBySelected", stories: stories.length > 0 ? stories : state.stories });
      } else if (sortBy === "unselected") {
        dispatch({ type: "sortByUnselected", stories: stories.length > 0 ? stories : state.stories });
      } else if (sortBy === null) {
        dispatch({ type: "sortByDate", stories: stories.length > 0 ? stories : state.stories });
      } else {
        dispatch({ type: "setStories", stories });
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stories, sortBy]
  );

  if (state.fetching) {
    return (
      <div className="StoryItemPlaceholder__Container">
        <div className="StoryItemPlaceholder__Left">
          <StoryItemPlaceholder />
          <StoryItemPlaceholder />
        </div>
        <div className="StoryItemPlaceholder__Right">
          <StoryItemPlaceholder />
        </div>
      </div>
    );
  }

  if (state.error) {
    return <ErrorMessage error={state.error} />;
  }

  const onStoryUpdate = updatedStory => {
    dispatch({ type: "storyUpdate", storyId: updatedStory.id, updates: updatedStory });
    setActiveStory(updatedStory);
  };

  const onStoryRemove = storyId => {
    setModalIsOpen(false);
    dispatch({ type: "storyRemove", storyId });
  };

  const handleThumbnailClick = goToIndex => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = state.stories.length - 1;
    }

    if (goToIndex > state.stories.length - 1) {
      target = 0;
    }

    setActiveStory(state.stories[target]);
    setModalIsOpen(true);
  };

  const handleStoryCheerToggle = (storyId, cheerForUser, cheersCount) => {
    state.stories = state.stories.map(story => (
      story.id !== storyId ? story : ({ ...story, cheerForUser, cheersCount })
    ));
  };

  const handleClose = () => {
    setModalIsOpen(false);
  };

  const handleCommentsChange = (story, numberOfComments) => {
    dispatch({ type: "storyCommentsUpdated", storyId: story.id, numberOfComments });
  };

  const selectStory = story => {
    onSelected(story);
    dispatch({ type: "storySelected", story });
  };

  const unselectStory = story => {
    onUnselected(story);
    dispatch({ type: "storyUnselected", story });
  };

  const shouldLazyLoading = state.pagination.currentPage !== state.pagination.totalPages
  && state.stories.length;

  return (
    <>
      <div className="Stories__container">
        {activeStoryQuestion && (
          <PostStory
            initialOpen
            preFilledQuestion={activeStoryQuestion}
            path="/profile"
          />
        )}
        {openShareStoryModal && createYourOwnStory && (
          <PostStory
            initialOpen
            path="/profile"
          />
        )}
        {activeStory && (
          <LightboxStories
            modalIsOpen={modalIsOpen}
            activeStory={activeStory}
            currentUserAnswered
            stories={state.stories}
            onCommentsChange={handleCommentsChange}
            onClose={handleClose}
            onStoryChange={handleThumbnailClick}
            onDelete={onStoryRemove}
            onStoryCheerToggle={handleStoryCheerToggle}
            onStoryUpdate={onStoryUpdate}
          />
        )}
        <div className="Stories__container--images">
          <StackGrid
            columnWidth={columnWidth}
            monitorImagesLoaded
          >
            {state.stories.map((story, index) => (
              <ErrorBoundary key={`${story.id}-${index}`}>
                <StoryThumbnail
                  index={index}
                  key={`${story.id}-${index}`}
                  story={story}
                  onClick={handleThumbnailClick}
                  selectStory={selectStory}
                  unselectStory={unselectStory}
                />
              </ErrorBoundary>
            ))}
            {shouldLazyLoading && (
              <LastIntersectedElement
                rootRef={feedRef}
                onNearScreen={() => dispatch({ type: "appendStart" })}
              />
            )}
          </StackGrid>
          {state.appending && (
            <Loader loading />
          )}
          {state.stories.length < 1 && (
            <div className="EmptyResults EmptyResults-feed">
              <p><Trans>No stories to show...</Trans></p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Stories;
